import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { App as CapApp } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { isPlayable, type CountryConfig, type StationV2 } from "@orvis/core";
import type { FirestoreLike } from "@orvis/data";
import { useChannels, useRadios } from "../queries.js";
import { stationKey, useFavorites } from "../state/favorites.js";
import { useNowPlaying } from "../state/nowPlaying.js";
import { useRecentlyPlayed } from "../state/recentlyPlayed.js";
import { OrvisTvChannels, hasNative, parsePlayLink } from "../native.js";

/**
 * App-wide glue to native surfaces that isn't tied to one screen:
 *   - Android TV home screen row (favorites, or live channels when there
 *     are none yet), kept in sync with the catalog + favorites
 *   - `orvis-play:<kind>/<key>` launch links from that row → play + navigate
 * Renders nothing.
 */
export function NativeBridges({ db, country, tv }: { db: FirestoreLike; country: CountryConfig; tv: boolean }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const now = useNowPlaying();
  const favorites = useFavorites();
  const recent = useRecentlyPlayed();
  const tvQ = useChannels(db, country.code);
  const radioQ = useRadios(db, country.code);

  const byKey = useMemo(() => {
    const m = new Map<string, { kind: "tv" | "radio"; station: StationV2 }>();
    (tvQ.data?.open ?? []).forEach((s, i) => m.set(`tv:${stationKey(s, i)}`, { kind: "tv", station: s }));
    (radioQ.data?.open ?? []).forEach((s, i) => m.set(`radio:${stationKey(s, i)}`, { kind: "radio", station: s }));
    return m;
  }, [tvQ.data, radioQ.data]);

  // --- Launch links -------------------------------------------------------
  useEffect(() => {
    if (!Capacitor.isNativePlatform() || byKey.size === 0) return;
    const open = (url: string | undefined) => {
      const link = url ? parsePlayLink(url) : undefined;
      const hit = link ? byKey.get(`${link.kind}:${link.key}`) : undefined;
      if (!link || !hit) return;
      recent.push(link.kind, link.key);
      if (link.kind === "tv") now.playTv(hit.station);
      else now.playRadio(hit.station);
      navigate(link.kind === "tv" ? "/tv" : "/radio");
    };
    let handled = false;
    void CapApp.getLaunchUrl().then((r) => {
      if (!handled) open(r?.url);
      handled = true;
    });
    const sub = CapApp.addListener("appUrlOpen", (e) => open(e.url));
    return () => void sub.then((h) => h.remove());
    // Only once the catalogs are in; launch links before that would miss.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [byKey.size > 0]);

  // --- Android TV home row ------------------------------------------------
  const favTv = favorites.list("tv");
  const favRadio = favorites.list("radio");
  useEffect(() => {
    if (!tv || !hasNative("OrvisTvChannels") || byKey.size === 0) return;
    const items = [
      ...favTv.map((k) => ({ key: k, hit: byKey.get(`tv:${k}`) })),
      ...favRadio.map((k) => ({ key: k, hit: byKey.get(`radio:${k}`) })),
    ].filter((x): x is { key: string; hit: { kind: "tv" | "radio"; station: StationV2 } } => !!x.hit);
    const useFavs = items.length > 0;
    const list = useFavs
      ? items
      : (tvQ.data?.open ?? [])
          .map((s, i) => ({ key: stationKey(s, i), hit: { kind: "tv" as const, station: s } }))
          .filter((x) => isPlayable(x.hit.station))
          .slice(0, 12);
    void OrvisTvChannels.sync({
      title: useFavs ? t("favorites") : t("trending_channels"),
      items: list.map(({ key, hit }) => ({
        key,
        kind: hit.kind,
        name: hit.station.name,
        description: hit.station.description,
        image: hit.station.logo || undefined,
      })),
    }).catch((err) => console.warn("[tv-row]", err));
  }, [tv, byKey, favTv, favRadio, tvQ.data, t]);

  return null;
}
